import React, { Component } from "react";

import Backend from "../utils/Backend";
import Notify from "../utils/Notify";

export default class DeliverySettings extends Component {
  constructor(props) {
    super(props);

    this.state = {
      delivery_distances: [],
      isLoading: false,
    };
  }

  componentDidMount() {
    this._getDeliveryDistances();
  }

  // ✅ helper: cents -> display
  _toEuro(cents) {
    if (cents === null || cents === undefined || cents === "") return "";
    return Number(cents) / 100;
  }

  // ✅ helper: display -> cents (null if blank)
  _toCents(value) {
    if (value === "" || value === null || value === undefined) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    // allow 0, decimals
    return Math.round(n * 100);
  }

  // ✅ enforce: fee==0 => free_delivery true, free_delivery true => fee=0
  _normalizeRow(row) {
    const fee = row?.fee ?? null;

    // if fee is exactly 0 (or "0"), it is free delivery
    const feeIsZero = fee === 0 || fee === "0";

    const free_delivery = feeIsZero ? true : !!row?.free_delivery;

    return {
      ...row,
      free_delivery,
      fee: free_delivery ? 0 : fee, // force 0 if free
    };
  }

  _getDeliveryDistances() {
    this.setState({ isLoading: true });

    Backend.getDeliveryDistances()
      .then((delivery_distances) => {
        const normalized = (delivery_distances || []).map((d) =>
          this._normalizeRow(d)
        );
        this.setState({ delivery_distances: normalized, isLoading: false });
      })
      .catch((error) => {
        Notify.error(error.message);
        this.setState({ isLoading: false });
      });
  }

  _isFormValid() {
    const { delivery_distances } = this.state;

    const toDistance = delivery_distances?.every(
      (d) => d.ends !== null && d.ends !== undefined && d.ends !== ""
    );

    // Fee required only if NOT free_delivery
    const feeDistance = delivery_distances?.every(
      (d) =>
        d.free_delivery ||
        (d.fee !== null && d.fee !== undefined && d.fee !== "")
    );

    const driverFee = delivery_distances?.every(
      (d) => d.driver_fee !== null && d.driver_fee !== undefined
    );

    const isFromEqualToEndDistance = delivery_distances.filter(
      (d, index) => delivery_distances[index].starts == delivery_distances[index].ends
    );

    const isFromBiggerThanEndDistance = delivery_distances.filter(
      (d) => parseInt(d.starts) > parseInt(d.ends)
    );

    let error = null;

    if (!toDistance) {
      error = "Please enter a valid ending point.";
    } else if (!feeDistance) {
      error = "Please enter a valid fee (or mark as Free Delivery).";
    } else if (!driverFee) {
      error = "Please enter a valid driver fee.";
    } else if (isFromEqualToEndDistance && isFromEqualToEndDistance.length !== 0) {
      error = "Starting point cannot be equal to ending point.";
    } else if (isFromBiggerThanEndDistance && isFromBiggerThanEndDistance.length !== 0) {
      error = "Ending point cannot be smaller than starting point.";
    }

    if (error) {
      Notify.error(error);
      return false;
    }

    return true;
  }

  _createDeliveryDistances() {
    let { delivery_distances } = this.state;

    if (!this._isFormValid()) return;

    // ✅ First row always starts from 0
    if (delivery_distances.length >= 1) {
      delivery_distances[0]["starts"] = 0;
    }

    // ✅ normalize payload before sending
    delivery_distances = (delivery_distances || []).map((d) => this._normalizeRow(d));

    this.setState({ isLoading: true });

    Backend.createDeliveryDistances(delivery_distances)
      .then(() => {
        this._getDeliveryDistances();
        Notify.success("Delivery distances have been updated successfully.");
      })
      .catch((error) => {
        Notify.error(error.message);
        this.setState({ isLoading: false });
      });
  }

  render() {
    let { delivery_distances, isLoading } = this.state;

    return (
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <div className="card card-custom">
              <div className="card-header flex-wrap border-0 pt-6 pb-0">
                <div className="card-title">
                  <h3 className="card-label">Delivery Settings</h3>
                </div>
              </div>

              <form action="javascript:;">
                <div className="card-body">
                  <div className="form-group row">
                    <div className="col-lg-12">
                      {delivery_distances?.map((deliveryDistanceRaw, index) => {
                        // ✅ always use normalized view of the row
                        const deliveryDistance = this._normalizeRow(deliveryDistanceRaw);

                        // keep state in sync if normalize changed anything
                        if (
                          deliveryDistance.free_delivery !== deliveryDistanceRaw.free_delivery ||
                          deliveryDistance.fee !== deliveryDistanceRaw.fee
                        ) {
                          delivery_distances[index] = deliveryDistance;
                        }

                        const fromDistance = index === 0 ? 0 : deliveryDistance?.starts;

                        return (
                          <div key={index} className="form-group row align-items-center">
                            {/* From */}
                            <div className="col-md-2">
                              <div className="form-group">
                                <label>From</label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="starts"
                                    value={fromDistance}
                                    disabled={true}
                                  />
                                  <div className="input-group-append">
                                    <span className="input-group-text">KM</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* To */}
                            <div className="col-md-2">
                              <div className="form-group">
                                <label>To</label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="ends"
                                    value={deliveryDistance?.ends ?? ""}
                                    onChange={(e) => {
                                      const ends = e.target.value === "" ? "" : parseInt(e.target.value, 10);

                                      delivery_distances[index].ends = ends;

                                      if (delivery_distances[index + 1]) {
                                        delivery_distances[index + 1].starts = ends === "" ? "" : ends;
                                      }

                                      this.setState({ delivery_distances });
                                    }}
                                  />
                                  <div className="input-group-append">
                                    <span className="input-group-text">KM</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Free Delivery */}
                            <div className="col-md-2">
                              <div className="form-group">
                                <label>Free Delivery</label>
                                <div className="checkbox-inline mt-2">
                                  <label className="checkbox checkbox-outline checkbox-success">
                                    <input
                                      type="checkbox"
                                      checked={!!deliveryDistance.free_delivery}
                                      onChange={(e) => {
                                        const checked = e.target.checked;

                                        delivery_distances[index].free_delivery = checked;

                                        if (checked) {
                                          // ✅ force zero & lock input
                                          delivery_distances[index].fee = 0;
                                        } else {
                                          // ✅ require user to enter fee again
                                          delivery_distances[index].fee = null;
                                        }

                                        this.setState({ delivery_distances });
                                      }}
                                    />
                                    <span />
                                    Free
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Delivery Fee */}
                            <div className="col-md-3">
                              <div className="form-group">
                                <label>Delivery Fee *</label>
                                <div className="input-group">
                                  <div className="input-group-prepend">
                                    <span className="input-group-text">€</span>
                                  </div>

                                  <input
                                    type="number"
                                    className="form-control"
                                    name="fee"
                                    disabled={!!deliveryDistance.free_delivery}
                                    value={deliveryDistance.free_delivery ? 0 : this._toEuro(deliveryDistance.fee)}
                                    onChange={(e) => {
                                      // if free_delivery true, ignore edits (though disabled already)
                                      if (delivery_distances[index].free_delivery) return;

                                      const cents = this._toCents(e.target.value);
                                      delivery_distances[index].fee = cents;

                                      // ✅ if user typed 0 => auto mark free delivery
                                      if (cents === 0) {
                                        delivery_distances[index].free_delivery = true;
                                        delivery_distances[index].fee = 0;
                                      } else {
                                        delivery_distances[index].free_delivery = false;
                                      }

                                      this.setState({ delivery_distances });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Driver Fee */}
                            <div className="col-md-3">
                              <div className="form-group">
                                <label>Driver Fee *</label>
                                <div className="input-group">
                                  <div className="input-group-prepend">
                                    <span className="input-group-text">€</span>
                                  </div>
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="driver_fee"
                                    value={this._toEuro(deliveryDistance?.driver_fee)}
                                    onChange={(e) => {
                                      delivery_distances[index].driver_fee = this._toCents(e.target.value);
                                      this.setState({ delivery_distances });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Delete last row */}
                            {index === delivery_distances.length - 1 && (
                              <div className="col-md-6">
                                <a
                                  href="javascript:;"
                                  className="btn btn-sm font-weight-bolder btn-light-danger w-100"
                                  onClick={() => {
                                    delivery_distances.splice(index, 1);
                                    this.setState({ delivery_distances });
                                  }}
                                >
                                  <i className="la la-trash-o" />
                                  Delete
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add Another */}
                  <div className="form-group row">
                    <label className="col-lg-6 col-form-label text-right" />
                    <div className="col-lg-6">
                      <a
                        href="javascript:;"
                        className="btn btn-sm font-weight-bolder btn-primary w-100 mt-minus-140"
                        onClick={() => {
                          const last = delivery_distances[delivery_distances.length - 1];
                          const starts = last?.ends ?? 0;
                          const ends = starts;

                          delivery_distances.push({
                            starts,
                            ends,
                            fee: null,
                            driver_fee: null,
                            free_delivery: false,
                          });

                          this.setState({ delivery_distances });
                        }}
                      >
                        <i className="la la-plus" />
                        Add Another Delivery Option
                      </a>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    type="submit"
                    className="btn btn-primary mr-2"
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      this._createDeliveryDistances();
                    }}
                  >
                    Save Settings
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
